import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ChartOfAccountLeafSelect } from "@/components/accounts/ChartOfAccountLeafSelect";
import {
  AccountReportFilterActions,
  AccountReportPageHeader,
  AccountReportTableShell,
} from "@/components/accounts/AccountReportShell";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { renderBalance } from "@/lib/accounts/ledgerReportDisplay";
import { formatReportDate, money } from "@/lib/accounts/ledgerReportUtils";
import {
  downloadCompanyLedgerExcel,
  downloadCompanyLedgerPdf,
  fetchCompanyLedger,
  type CompanyLedger,
} from "@/lib/accounts/companyLedgerStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { BookOpen } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

type LedgerReportKind = "company_ledger" | "general_ledger";

function formatCashAmount(value: number, emptyAsBlank = true, symbol = ""): string {
  if (emptyAsBlank && Math.abs(value) < 0.005) return "";
  const num = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return symbol ? `${symbol} ${num}` : num;
}

const companyLedgerSheetClass =
  "w-full border-collapse text-sm [&_th]:border [&_th]:border-neutral-800 [&_th]:bg-white [&_th]:px-2.5 [&_th]:py-2 [&_th]:text-center [&_th]:text-xs [&_th]:font-bold [&_th]:text-neutral-900 [&_td]:border [&_td]:border-neutral-800 [&_td]:bg-white [&_td]:px-2.5 [&_td]:py-1.5 [&_td]:align-top [&_td]:text-neutral-900";

const REPORT_KIND_OPTIONS: { value: LedgerReportKind; label: string }[] = [
  { value: "company_ledger", label: "Company Ledger" },
  { value: "general_ledger", label: "General Ledger" },
];

function validationMessage(error: unknown): string | null {
  if (!isAxiosError(error) || error.response?.status !== 422) return null;
  const data = error.response?.data as {
    message?: string;
    errors?: Record<string, string[]>;
  };
  const firstFieldError = data?.errors
    ? Object.values(data.errors).flat().find((msg) => Boolean(msg))
    : null;
  return firstFieldError || data?.message || "Invalid filter selection.";
}

const CompanyLedgerPage = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "chart_of_account_can_view_any") || can(user, "bank_can_view_any");

  const [isForbidden, setIsForbidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [ledger, setLedger] = useState<CompanyLedger | null>(null);
  const [reportKind, setReportKind] = useState<LedgerReportKind>("general_ledger");
  const [accountId, setAccountId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "";
  const isGeneralLedger = reportKind === "general_ledger";
  const hasAccount = accountId.trim() !== "";
  const isDailyMode = ledger?.mode === "daily_vouchers";

  const activeFilters = useMemo(
    () => ({
      accountId: isGeneralLedger && hasAccount ? accountId : null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }),
    [isGeneralLedger, hasAccount, accountId, dateFrom, dateTo],
  );

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  const applyFilters = useCallback(async () => {
    if (!canView) return;

    if (isGeneralLedger) {
      if (!hasAccount) {
        toast.error("Select an account for general ledger.");
        return;
      }
    } else if (!dateFrom || !dateTo) {
      toast.error("Select from and to dates for company ledger.");
      return;
    }

    setLoading(true);
    setIsForbidden(false);
    try {
      const data = await fetchCompanyLedger(activeFilters);
      setLedger(data);
      setHasApplied(true);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      const message = validationMessage(error);
      if (message) {
        toast.error(message);
        return;
      }
      console.error("[CompanyLedger] Load failed", error);
      toast.error("Could not load report.");
    } finally {
      setLoading(false);
    }
  }, [canView, isGeneralLedger, hasAccount, dateFrom, dateTo, activeFilters]);

  const exportReport = useCallback(
    async (format: "pdf" | "excel") => {
      if (!hasApplied || exporting) return;
      setExporting(format);
      try {
        if (format === "pdf") {
          await downloadCompanyLedgerPdf(activeFilters);
          toast.success("PDF downloaded.");
        } else {
          await downloadCompanyLedgerExcel(activeFilters);
          toast.success("Excel downloaded.");
        }
      } catch (error) {
        console.error(`[CompanyLedger] ${format} export failed`, error);
        toast.error(format === "pdf" ? "Could not download PDF." : "Could not download Excel.");
      } finally {
        setExporting(null);
      }
    },
    [hasApplied, exporting, activeFilters],
  );

  const changeReportKind = (value: LedgerReportKind) => {
    setReportKind(value);
    setAccountId("");
    setLedger(null);
    setHasApplied(false);
  };

  const resetFilters = () => {
    setReportKind("general_ledger");
    setAccountId("");
    setDateFrom("");
    setDateTo("");
    setLedger(null);
    setHasApplied(false);
  };

  const accountLabel = ledger?.account
    ? `${ledger.account.code ? `${ledger.account.code} — ` : ""}${ledger.account.name}`
    : null;

  const colSpan = isDailyMode ? 6 : 6;

  if (!canView || isForbidden) {
    return (
      <Layout>
        <PermissionDenied />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        <AccountReportPageHeader
          icon={<BookOpen className="h-5 w-5 text-primary" />}
          title="Ledgers"
          description="Company ledger is a cash/bank book with money in, money out and running balance. General ledger shows a full account ledger."
        />

        <div className="rounded-2xl border border-border/55 bg-card/80 p-4 shadow-sm ring-1 ring-border/25">
          <div
            className={cn(
              "grid grid-cols-1 gap-3 sm:grid-cols-2",
              isGeneralLedger ? "lg:grid-cols-4" : "lg:grid-cols-3",
            )}
          >
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Report type *</Label>
              <Select
                value={reportKind}
                onValueChange={(value) => changeReportKind(value as LedgerReportKind)}
              >
                <SelectTrigger className="glass-input h-9 text-xs">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_KIND_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isGeneralLedger ? (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Account *</Label>
                <ChartOfAccountLeafSelect
                  value={accountId}
                  onChange={setAccountId}
                  placeholder="Select account"
                />
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">
                From date{isGeneralLedger ? "" : " *"}
              </Label>
              <DatePartsPicker
                value={dateFrom}
                onChange={setDateFrom}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                maxDate={dateTo ? new Date(`${dateTo}T00:00:00`) : undefined}
                placeholder="Select from date"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">
                To date{isGeneralLedger ? "" : " *"}
              </Label>
              <DatePartsPicker
                value={dateTo}
                onChange={setDateTo}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                minDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined}
                placeholder="Select to date"
                align="end"
              />
            </div>
          </div>

          <AccountReportFilterActions
            hasApplied={hasApplied}
            loading={loading}
            exporting={exporting}
            onApply={() => void applyFilters()}
            onReset={resetFilters}
            onExport={(format) => void exportReport(format)}
            statusText={
              hasApplied ? (
                <>
                  {isDailyMode ? (
                    <>Showing <span className="font-semibold text-foreground">company ledger</span></>
                  ) : accountLabel ? (
                    <>
                      Showing general ledger for{" "}
                      <span className="font-semibold text-foreground">{accountLabel}</span>
                    </>
                  ) : (
                    "Showing general ledger"
                  )}
                  {dateFrom ? (
                    <>
                      {" · From "}
                      <span className="font-semibold text-foreground">{formatReportDate(dateFrom)}</span>
                    </>
                  ) : null}
                  {dateTo ? (
                    <>
                      {" · To "}
                      <span className="font-semibold text-foreground">{formatReportDate(dateTo)}</span>
                    </>
                  ) : null}
                </>
              ) : isGeneralLedger ? (
                "Select an account (dates optional), then Apply filter."
              ) : (
                "Select from and to dates for the company cash book, then Apply filter."
              )
            }
          />
        </div>

        <AccountReportTableShell
          loading={loading}
          hasApplied={hasApplied}
          notAppliedMessage={
            isGeneralLedger
              ? "No report loaded yet. Choose an account, then apply."
              : "No report loaded yet. Choose from and to dates, then apply."
          }
        >
          {!ledger ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No data.</p>
          ) : isDailyMode ? (
            <div className="space-y-6">
              <div className="overflow-x-auto rounded-sm border border-neutral-800 bg-white">
                <table className={companyLedgerSheetClass}>
                  <thead>
                    <tr>
                      <th className="min-w-[6.5rem]">Date</th>
                      <th className="min-w-[7rem]">Ref</th>
                      <th className="min-w-[16rem]">Particulars</th>
                      <th className="min-w-[8rem]">Receipt</th>
                      <th className="min-w-[8rem]">Disbursement</th>
                      <th className="min-w-[8.5rem]">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan={3} className="font-semibold">
                        Opening Balance
                      </td>
                      <td className="text-right tabular-nums" />
                      <td className="text-right tabular-nums" />
                      <td className="text-right tabular-nums font-semibold">
                        {ledger.totals.opening_balance_side === "Cr"
                          ? `(${formatCashAmount(ledger.totals.opening_balance, false, currencySymbol)})`
                          : formatCashAmount(ledger.totals.opening_balance, false, currencySymbol)}
                      </td>
                    </tr>

                    {ledger.lines.length === 0 ? (
                      <tr>
                        <td colSpan={colSpan} className="py-10 text-center text-neutral-500">
                          No cash/bank transactions in this period.
                        </td>
                      </tr>
                    ) : (
                      ledger.lines.map((line) => (
                        <tr key={line.id}>
                          <td className="whitespace-nowrap">{formatReportDate(line.date)}</td>
                          <td className="whitespace-nowrap font-mono text-xs">{line.voucher || ""}</td>
                          <td>
                            <div className="space-y-0.5">
                              {line.particulars.length > 0 ? (
                                line.particulars.map((text, idx) => (
                                  <p
                                    key={`${line.id}-p-${idx}`}
                                    className={cn(
                                      "leading-snug",
                                      idx === 0 ? "font-medium text-neutral-900" : "text-xs text-neutral-600",
                                    )}
                                  >
                                    {text}
                                  </p>
                                ))
                              ) : (
                                <span className="text-neutral-500">—</span>
                              )}
                            </div>
                          </td>
                          <td className="text-right tabular-nums text-neutral-900">
                            {formatCashAmount(line.money_in, true, currencySymbol)}
                          </td>
                          <td className="text-right tabular-nums font-medium text-red-600">
                            {line.money_out >= 0.005
                              ? `(${formatCashAmount(line.money_out, false, currencySymbol)})`
                              : ""}
                          </td>
                          <td className="text-right tabular-nums text-neutral-900">
                            {line.balance_side === "Cr"
                              ? `(${formatCashAmount(line.balance, false, currencySymbol)})`
                              : formatCashAmount(line.balance, false, currencySymbol)}
                          </td>
                        </tr>
                      ))
                    )}

                    <tr>
                      <td colSpan={3} className="font-bold">
                        Totals
                      </td>
                      <td className="text-right tabular-nums font-bold text-neutral-900">
                        {formatCashAmount(ledger.totals.money_in, false, currencySymbol)}
                      </td>
                      <td className="text-right tabular-nums font-bold text-red-600">
                        ({formatCashAmount(ledger.totals.money_out, false, currencySymbol)})
                      </td>
                      <td className="text-right tabular-nums font-bold text-neutral-900">
                        {ledger.totals.balance_side === "Cr"
                          ? `(${formatCashAmount(ledger.totals.balance, false, currencySymbol)})`
                          : formatCashAmount(ledger.totals.balance, false, currencySymbol)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="font-bold">
                        Closing Balance
                      </td>
                      <td className="text-right tabular-nums font-bold text-neutral-900">
                        {ledger.totals.balance_side === "Cr"
                          ? `(${formatCashAmount(ledger.totals.balance, false, currencySymbol)})`
                          : formatCashAmount(ledger.totals.balance, false, currencySymbol)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {ledger.receivable_lines.length > 0 ? (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">Pending Receivables</h3>
                  <p className="text-xs text-muted-foreground">
                    Shown separately — not included in receipt/disbursement or running balance.
                  </p>
                  <div className="overflow-x-auto rounded-sm border border-neutral-800 bg-white">
                    <table className={companyLedgerSheetClass}>
                      <thead>
                        <tr>
                          <th className="min-w-[6.5rem]">Date</th>
                          <th className="min-w-[7rem]">Ref</th>
                          <th className="min-w-[16rem]">Particulars</th>
                          <th className="min-w-[8rem]">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.receivable_lines.map((line) => (
                          <tr key={`recv-${line.id}`}>
                            <td className="whitespace-nowrap">{formatReportDate(line.date)}</td>
                            <td className="whitespace-nowrap font-mono text-xs">{line.voucher || ""}</td>
                            <td>
                              <div className="space-y-0.5">
                                {line.particulars.map((text, idx) => (
                                  <p
                                    key={`${line.id}-r-${idx}`}
                                    className={cn(
                                      "leading-snug",
                                      idx === 0 ? "font-medium text-neutral-900" : "text-xs text-neutral-600",
                                    )}
                                  >
                                    {text}
                                  </p>
                                ))}
                              </div>
                            </td>
                            <td className="text-right tabular-nums font-medium text-emerald-700">
                              {formatCashAmount(line.amount, false, currencySymbol)}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={3} className="font-bold">
                            Total Pending Receivables
                          </td>
                          <td className="text-right tabular-nums font-bold text-emerald-700">
                            {formatCashAmount(ledger.summary?.receivable ?? 0, false, currencySymbol)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date / Voucher #</TableHead>
                    <TableHead>Particulars</TableHead>
                    <TableHead>From / To</TableHead>
                    <TableHead className="text-right">Debit{currencySymbol ? ` (${currencySymbol})` : ""}</TableHead>
                    <TableHead className="text-right">Credit{currencySymbol ? ` (${currencySymbol})` : ""}</TableHead>
                    <TableHead className="text-right">
                      Balance{currencySymbol ? ` (${currencySymbol})` : ""}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-muted/30">
                    <TableCell className="font-semibold text-foreground" colSpan={3}>
                      Opening Balance
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground" />
                    <TableCell className="text-right tabular-nums text-muted-foreground" />
                    <TableCell className="text-right tabular-nums font-semibold">
                      {renderBalance(ledger.totals.opening_balance, ledger.totals.opening_balance_side)}
                    </TableCell>
                  </TableRow>

                  {ledger.lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colSpan} className="py-10 text-center text-sm text-muted-foreground">
                        No posted entries in this period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    ledger.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell className="min-w-[8rem] whitespace-nowrap">
                          <div className="text-sm text-foreground">{formatReportDate(line.date)}</div>
                          <div className="font-mono text-[11px] text-muted-foreground">{line.voucher || ""}</div>
                          {line.voucher_type ? (
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              {line.voucher_type}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="min-w-[10rem] max-w-[18rem]">
                          <div className="space-y-0.5">
                            {line.particulars.length > 0 ? (
                              line.particulars.map((text, idx) => (
                                <p
                                  key={`${line.id}-p-${idx}`}
                                  className={cn(
                                    "leading-snug",
                                    idx === 0
                                      ? "text-xs font-medium text-foreground"
                                      : "text-[11px] text-muted-foreground",
                                  )}
                                >
                                  {text}
                                </p>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="min-w-[8rem] max-w-[14rem] text-xs leading-snug text-muted-foreground">
                          {line.from_to || ""}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                          {money(line.debit, true)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-rose-700 dark:text-rose-400">
                          {money(line.credit, true)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {renderBalance(line.balance, line.balance_side)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={3} className="font-semibold text-foreground">
                      Closing Balance
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {money(ledger.totals.debit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {money(ledger.totals.credit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {renderBalance(ledger.totals.balance, ledger.totals.balance_side)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </AccountReportTableShell>
      </div>
    </Layout>
  );
};

export default CompanyLedgerPage;
